<template lang="pug">
  div
    // Quiz main information form
    div#quiz-info-view(v-if="!isEdit")
      // Title
      el-row
        el-col(:span="24")
          h2#title-info {{ form.title }}
          div#content
            p.ql-editor.margin-top-15.padding-right-40(
              v-html="form.description"
            ) 
            hr#divider
      // Buttons
      el-row
        el-col#buttons(:span="24")
          el-button-group(style="float: right;")
            el-button(
              class="delete-button"
              icon="el-icon-delete"
              @click="deleteQuiz")   
                small Erase
            el-button(
              class="primary-button"
              @click="isEdit=true"
              icon="el-icon-edit") 
                small Edit


    div(v-else)
      el-form#quiz-info(
        :model="form"
        :rules="rules"
        :disabled="processing"
        ref="form"
        label-position="top"
      )
        // Title
        el-form-item(
          style="width: 45%;"
          prop="title"
          )
            label(:class="$style.label") Quiz Title
            el-input(
              placeholder="Enter Quiz Title"
              v-model="form.title")          

        // Description
        el-form-item(prop="description")
          label(:class="$style.label") Description
          rich-editor(
            v-model="form.description")

        // Buttons
        el-row
          el-col#buttons(:span="24")
            el-button-group(style="float: right;")
              el-button(
                icon="el-icon-error"
                @click="cancelEdit")   
                  small Cancel
              el-button(
                class="primary-button"
                icon="el-icon-document"
                @click="onSubmitValidate('form')") 
                  small Save

</template>

<script>
import RichEditor from '@/components/utils/RichEditor'
import utilMixin from '@/components/mixins/utilMixin'
import {MAX_LENGTH} from '@/defines/constants'

export default {
  name: 'QuizInfoView',
  
  components: {
    RichEditor,
  },

  mixins: [utilMixin],

  data() {
    return {
      userData: { 
        userId: this.$store.state.canvasData.userId,
      },
      form: {
        title: '',
        description: '',
      },
      origForm: {
        title: '',
        description: '',
      },
      rules: {
        title: [
          { allowspecial: false, max: MAX_LENGTH.TITLE, validator: this.mValidateText, trigger: 'blur' },
        ],
        description: [
          { max: MAX_LENGTH.CONTENT, validator: this.mValidateText, trigger: 'blur' },
        ],    
      },    
      processing: true, 
      isEdit: false,
      hasQuiz: false,
    }
  },

 mounted() {
    this.fetchQuiz()
  }, 

  methods: {
    fetchQuiz() {
      this.$emit('onProcessing',true)

      // Workaround for determining module id from parent window url
      const parentUrl = (window.location != window.parent.location)
              ? document.referrer
              : document.location.href
      const urlParams = this.mGetUrlVar(parentUrl)
      const itemId = urlParams['items'] || urlParams['assignments']

      this.axios.get('/api/user/' + this.userData.userId + '/quiz/' + itemId)
        .then((result) => {
          this.axios.defaults.headers.common['X-CSRF-TOKEN'] = result.data.csrfToken
          this.form = {title: result.data.object.title, description: result.data.object.description}
          this.origForm = {title: result.data.object.title, description: result.data.object.description}
          const quizData = {
            id:result.data.object.id, 
            resourceId:result.data.object.c_resource_id, 
            itemId:result.data.object.c_item_id,
            isGraded:result.data.isGraded,
            isEnrolled:result.data.isEnrolled,
          }
          this.$store.commit('setQuiz', quizData)
          this.isEdit = ( this.form.title.length === 0 && this.form.description.length === 0)
          this.hasQuiz = !this.isEdit
          this.processing = false
          this.$emit('onProcessing', false)

          const availablePoints = result.data.availablePoints !== undefined? parseInt(result.data.availablePoints) : 9999
          if (quizData.isGraded && availablePoints <= 0) {
            const msg = '<span style="font-size: 14px">This <strong>Assignment</strong> has zero points. Please set this module\'s <strong>Points</strong> on Canvas\' <strong>Edit Assignment</strong> modal.'
            this.$alert(msg, 'Zero Available Points', {
              dangerouslyUseHTMLString: true,
              confirmButtonText: 'OK',
              confirmButtonClass: 'primary-button',
            }).then(() => {})
          }
        })
        .catch((error) => {
          this.processing = false
          this.mNotification({error: error, message: 'Error fetching quiz data.'})
        })
    },

    onSubmitValidate(formName) {
      this.$refs[formName].validate((valid) => {
        if (valid) {
          this.processing = true
          this.$emit('onProcessing', true)
          const config = { params: this.userData }
          config.params.title = this.form.title
          config.params.description = this.form.description
          this.updateQuiz(config)
        } else {
          return false
        }
      })
    },

    updateQuiz(config) {
      this.axios.post('/api/user/' + this.userData.userId + '/quiz', config)
        .then((result) => {
          if (result.data.object != undefined ) {
              this.form = result.data.object
              this.origForm = {title: result.data.object.title, description: result.data.object.description}
              const quizData = {
                id:result.data.object.id, 
                resourceId:result.data.object.c_resource_id, 
                isGraded:result.data.isGraded,
                isEnrolled:result.data.isEnrolled,
              }
              this.$store.commit('setQuiz', quizData)
              this.hasQuiz = true
              this.mNotification( {message: 'Your Quiz has been saved.'})
          }
          this.processing = false
          this.$emit('onProcessing', false)
          this.isNew = this.isEdit = false
        })
        .catch((error) => {
          this.processing = false
          this.mNotification({error: error, message: 'Your Quiz was not saved.'})
        })
    },

    deleteQuiz() {
      this.$emit('onProcessing',true)
      this.axios.delete('/api/user/' + this.userData.userId + '/quiz')
        .then(() => {
          this.mNotification( {message: 'Your Quiz info has been reset.'})
          this.form = this.origForm = {}
          this.isEdit = true
          this.hasQuiz = false
          this.processing = false
          this.$emit('onProcessing', false)
          this.$emit('onQuizDelete')
        })
        .catch((error) => {
          this.processing = false
          this.mNotification( {error: error, message: 'There was an error deleting your quiz.'} )
        })
    },

    cancelEdit() {
      if (this.hasQuiz) {
        this.isEdit = false
        for (let key in this.origForm) {
          this.form[key] = this.origForm[key]
        }
      }
    },

    copyToClipboard() {
      const launchUrl = [window.location.origin, '/launch/', this.$store.state.quizData.id].join('')
      let tempInput = document.getElementById('clipboard')

      if (tempInput === null) {
        tempInput = document.createElement('input')
        tempInput.type = 'text'
        tempInput.setAttribute('id', 'clipboard')
        tempInput.style.position = 'fixed'
        tempInput.style.zIndex = '-9999'
        document.body.append(tempInput)
      }

      tempInput.value = launchUrl
      tempInput.select()
      tempInput.setSelectionRange(0, 99999)
      document.execCommand('copy')

      this.$notify.success({
        title: 'Success',
        message: 'Launch URL is copied to clipboard.<br>Click <a href="/how-to-export" target="_blank">here</a> to view the full export instructions.',
        dangerouslyUseHTMLString: true,
        position: 'bottom-right',
        duration: 25000,
      })
    },
  },
}
</script>

<style module>
  label {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 12px;
    color: #757575;
  }  

  #buttons {
    margin-top: 10px;
  }

  #title-info {
    color: #303133;
    font-size: 2em;
  }

  #description {
    color: #606266;
    font-size: 14px;
  }

  #buttonpanel {
    position: absolute;
    right: 15px;
    bottom: 20px;
  }

  #content {
    text-align: justify;
    color: #606266;
    margin-top: 2%;
    min-height: 100px;
  }

  #divider {
    background-color: #DCDFE6;
  }

  .ql-picker-options {
    line-height: 20px;
  }

  .ql-toolbar {
    line-height: 0px;
  }

  .delete-button {
    background-color:#fff;
    color: #ef5350;
    border-color: #ef5350;
    min-width: 100px;
  }

  .delete-button:hover {
    background-color:#ef5350;
    color: #fff;
    border-color: #ef5350;
  }

  .primary-button {
    background-color:#409eff;
    border-color: #409eff;
    color: #fff;
    min-width: 100px;
    margin-left: 10px;
  }

  .primary-button:hover {
    background-color:#66b1ff;
    border-color: #66b1ff;
    color: #fff;
  }

  .normal-button {
    background-color:#fff;
    border-color: #dcdfe6;
    color: #606266;
    min-width: 100px;
  }

  .normal-button:hover {
    background-color:#ecf5ff;
    border-color: #c6e2ff;
    color: #409eff;
  }
</style>
